/**
 * Login Page
 * FDA 21 CFR Part 11 compliant authentication
 * Multi-study: user selects study before login
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { studiesApi } from '../services/api';
import { Lock, User, AlertCircle, Eye, EyeOff, BookOpen } from 'lucide-react';

interface ActiveStudy {
  _id: string;
  studyCode: string;
  studyName: string;
  status: string;
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [studyId, setStudyId] = useState('');
  const [studies, setStudies] = useState<ActiveStudy[]>([]);
  const [studiesLoading, setStudiesLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Return to the page the user came from, except auth pages (logging out from
  // /change-password must not send the next login straight back to it).
  const requestedFrom = (location.state as { from?: Location })?.from?.pathname;
  const from = requestedFrom && !['/login', '/change-password'].includes(requestedFrom)
    ? requestedFrom
    : '/dashboard';

  // Fetch active studies on mount
  useEffect(() => {
    const fetchStudies = async () => {
      try {
        const response = await studiesApi.listActive();
        setStudies(response.studies);
      } catch (err) {
        console.error('Failed to fetch studies:', err);
      } finally {
        setStudiesLoading(false);
      }
    };
    fetchStudies();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password || !studyId) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      await login(username, password, studyId);
      navigate(from, { replace: true });
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedStudy = studies.find(s => s._id === studyId);

  return (
    <div className="min-h-screen bg-opci-navy flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <img src="/opci-logo-white.png" alt="OPCI" className="w-20 h-20 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-bold text-white">OPCI EDC</h1>
          <p className="text-opci-navy-200 mt-2">Electronic Data Capture System</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Sign In</h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 font-medium">Authentication Failed</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Study Selector */}
            <div>
              <label htmlFor="study" className="block text-sm font-medium text-gray-700 mb-1">
                Study
              </label>
              <div className="relative">
                <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {studiesLoading ? (
                  <div className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-400">
                    Loading studies...
                  </div>
                ) : studies.length === 0 ? (
                  <div className="w-full pl-10 pr-4 py-3 border border-red-300 rounded-lg bg-red-50 text-red-500 text-sm">
                    No active studies found. Contact administrator.
                  </div>
                ) : (
                  <select
                    id="study"
                    value={studyId}
                    onChange={(e) => setStudyId(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-gold focus:border-transparent transition-all bg-white"
                    disabled={isSubmitting}
                    required
                  >
                    <option value="">Select a study...</option>
                    {studies.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.studyCode}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-gold focus:border-transparent transition-all"
                  placeholder="Enter your username"
                  autoComplete="username"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opci-gold focus:border-transparent transition-all"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !username.trim() || !password || !studyId}
              className="w-full py-3 px-4 bg-opci-gold text-white font-medium rounded-lg hover:bg-opci-gold-600 focus:ring-4 focus:ring-opci-gold-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Selected Study Badge */}
          {selectedStudy && (
            <div className="mt-4 p-3 bg-opci-navy/5 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                Signing into <span className="font-semibold text-opci-navy">{selectedStudy.studyCode}</span>
              </p>
            </div>
          )}

          {/* FDA Compliance Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              This system complies with FDA 21 CFR Part 11 requirements.
              All actions are logged for audit purposes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-opci-navy-200 text-sm mt-6">
          OPCI Core Lab &copy; {new Date().getFullYear()}
        </p>
      </div>
      </div>
    </div>
  );
};

export default Login;
